
import type { RankingElement } from '@empathybroker/tsclient-play/dist/models/ranking-element'
import type { PropType } from 'vue'
import type {
  ConfigurationsMessages,
  Dictionary,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
} from '../../models'
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum'
import { DomainDateUtils } from '@empathyco/playboard-core-domain'

import { defineComponent } from 'vue'

import { ColorEnum, PositionEnum } from '../../enums'

import { ConfigurationsUtils } from '../../utils/configurations.utils'
import { FiltersUtils } from '../../utils/filters.utils'

import Schedule from '../crud/schedule.component.vue'
import RegularFilterSelector from '../filters/regular-filter-selector.component.vue'
import { UiModal } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbKeywords } from '../shared/epb-keywords'

function getEmptyBaseElement(): Partial<RankingElement> {
  return {
    filters: {},
    schedule: {
      activationDate: undefined,
      deactivationDate: undefined,
    },
  }
}

export default defineComponent({
  name: 'QuickRanking',
  components: {
    EpbKeywords,
    EpbButton,
    UiModal,
    RegularFilterSelector,
    Schedule,
  },
  props: {
    resultIdField: {
      type: String,
      required: true,
    },
    filterConfigs: {
      type: Array as PropType<Array<RegularFilterConfig>>,
      required: true,
    },
    itemElements: {
      type: Array as PropType<Array<RankingElement>>,
      default: () => [],
    },
    disabled: {
      type: Boolean,
    },
    messages: {
      type: Object as PropType<ConfigurationsMessages>,
      required: true,
    },
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-quick-ranking',
      baseElement: getEmptyBaseElement(),
      resultIds: [],
      PositionEnum,
      RankingActionEnum,
    }
  },
  computed: {
    filters(): RegularFilter[] {
      return this.filterConfigs.map((config: RegularFilterConfig) => ({
        config,
        selectedValues: this.getFilterSelectedValues(config),
      }))
    },
    actionsDisabled(): boolean {
      return !this.resultIds.length || !!Object.keys(this.resultIdErrors).length
    },
    elements(): Partial<RankingElement>[] {
      return this.resultIds.map((resultId: string) => ({
        fieldName: this.resultIdField,
        fieldValue: resultId,
        filters: { ...this.baseElement.filters },
        schedule: { ...this.baseElement.schedule },
      }))
    },
    resultIdErrors(): Dictionary<string[]> {
      return this.elements.reduce(
        (resultIdErrors: Dictionary<string[]>, element: Partial<RankingElement>, index: number) => {
          const itemElement = this.itemElements.find(
            (itemEl: RankingElement) =>
              itemEl.fieldName === element.fieldName && itemEl.fieldValue === element.fieldValue,
          )
          if (
            !!itemElement &&
            ConfigurationsUtils.areFiltersOverlapped(itemElement.filters, element.filters) &&
            DomainDateUtils.arePeriodsOverlapped(
              {
                start: itemElement.schedule.activationDate,
                end: itemElement.schedule.deactivationDate,
              },
              { start: element.schedule.activationDate, end: element.schedule.deactivationDate },
            )
          ) {
            resultIdErrors[index] = [this.messages.quickRanking.overlappedResult]
          }
          return resultIdErrors
        },
        {},
      )
    },
  },
  methods: {
    open(): void {
      this.baseElement = getEmptyBaseElement()
      this.resultIds = []
      this.$refs.modal.open()
    },
    close(): void {
      this.$refs.modal.close()
    },
    getFilterSelectedValues(config: RegularFilterConfig): RegularFilterSelectedValues {
      const encodedId = FiltersUtils.getEncodedId(config)
      const encodedValues = this.baseElement.filters[encodedId]
      if (!encodedValues?.length) {
        return []
      }
      return encodedValues.map((encodedValue: string) =>
        FiltersUtils.decodeValue(config, encodedValue),
      )
    },
    onFilterChange({ config }: RegularFilter, values: RegularFilterSelectedValues): void {
      const encodedId = FiltersUtils.getEncodedId(config)
      const encodedValues = values.map((value: string) => FiltersUtils.encodeValue(config, value))
      this.$set(this.baseElement.filters, encodedId, encodedValues)
    },
    accept(action: RankingActionEnum): void {
      const elements: RankingElement[] = this.elements.map((element: Partial<RankingElement>) => ({
        ...element,
        action,
      }))
      this.$emit('accept', elements)
      this.close()
    },
    getActionColor(action: RankingActionEnum): ColorEnum {
      return {
        [RankingActionEnum.BOOST]: ColorEnum.GREEN_75,
        [RankingActionEnum.BURY]: ColorEnum.RED_75,
      }[action]
    },
    getActionText(action: RankingActionEnum): string {
      return this.messages.quickRanking.actions[action]
    },
  },
})
